import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = [
  { value: '#6366f1', name: 'Índigo' },
  { value: '#8b5cf6', name: 'Violeta' },
  { value: '#ec4899', name: 'Rosa' },
  { value: '#f43f5e', name: 'Rojo' },
  { value: '#f97316', name: 'Naranja' },
  { value: '#eab308', name: 'Amarillo' },
  { value: '#22c55e', name: 'Verde' },
  { value: '#14b8a6', name: 'Turquesa' },
  { value: '#3b82f6', name: 'Azul' },
  { value: '#6b7280', name: 'Gris' }
];

const ICONS = ['📚', '📖', '📝', '🎯', '🔬', '💻', '🌍', '🎨', '🎵', '⚽', '📊', '🧪'];

function CategoryManager({ user, categories, onUpdate, onClose }) {
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    parent_id: null,
    color: '#6366f1',
    icon: '📚'
  });
  const [isCreating, setIsCreating] = useState(false);

  const rootCategories = categories.filter(cat => !cat.parent_id);

  const getSubcategories = (parentId) => {
    return categories.filter(cat => cat.parent_id === parentId);
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newCategory.name,
          teacher_id: user.id,
          parent_id: newCategory.parent_id,
          color: newCategory.color,
          icon: newCategory.icon,
          order: categories.length
        })
      });

      if (response.ok) {
        setNewCategory({ name: '', parent_id: null, color: '#6366f1', icon: '📚' });
        setIsCreating(false);
        onUpdate();
      }
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleUpdateCategory = async (categoryId, updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teacher_id: user.id,
          ...updates
        })
      });

      if (response.ok) {
        setEditingCategory(null);
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('¿Eliminar esta categoría? (Solo si no tiene actividades asignadas)')) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/categories/${categoryId}?teacher_id=${user.id}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        onUpdate();
      } else {
        const data = await response.json();
        alert(data.detail || 'Error al eliminar categoría');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error al eliminar categoría');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">🗂️ Gestionar Categorías</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Create New Category */}
          {isCreating ? (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-indigo-200">
              <h3 className="font-semibold mb-3">Nueva Categoría</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Nombre de la categoría"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                
                <div className="flex gap-3">
                  <select
                    value={newCategory.parent_id || ''}
                    onChange={(e) => setNewCategory({ ...newCategory, parent_id: e.target.value || null })}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Sin categoría padre (Principal)</option>
                    {rootCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>

                  <select
                    value={newCategory.icon}
                    onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 text-2xl"
                  >
                    {ICONS.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(color => (
                    <button
                      key={color.value}
                      onClick={() => setNewCategory({ ...newCategory, color: color.value })}
                      className={`w-8 h-8 rounded-full border-2 ${
                        newCategory.color === color.value ? 'border-gray-900 scale-110' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleCreateCategory}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold"
                  >
                    Crear Categoría
                  </button>
                  <button
                    onClick={() => {
                      setIsCreating(false);
                      setNewCategory({ name: '', parent_id: null, color: '#6366f1', icon: '📚' });
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full mb-6 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold flex items-center justify-center gap-2"
            >
              <span className="text-xl">+</span> Nueva Categoría
            </button>
          )}

          {/* Category List */}
          <div className="space-y-2">
            {rootCategories.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <div className="text-6xl mb-4">📁</div>
                <p className="text-lg">No hay categorías creadas</p>
                <p className="text-sm">Crea tu primera categoría para organizar actividades</p>
              </div>
            ) : (
              rootCategories.map(category => (
                <div key={category.id} className="border rounded-lg overflow-hidden">
                  {/* Root Category */}
                  <div
                    className="p-3 flex items-center gap-3 hover:bg-gray-50"
                    style={{ borderLeft: `4px solid ${category.color}` }}
                  >
                    <span className="text-2xl">{category.icon}</span>
                    <span className="flex-1 font-semibold">{category.name}</span>
                    <button
                      onClick={() => setEditingCategory(category.id)}
                      className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-sm px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded"
                    >
                      🗑️
                    </button>
                  </div>

                  {/* Subcategories */}
                  {getSubcategories(category.id).map(subcat => (
                    <div
                      key={subcat.id}
                      className="p-3 pl-12 flex items-center gap-3 bg-gray-50 border-t hover:bg-gray-100"
                      style={{ borderLeft: `4px solid ${subcat.color}` }}
                    >
                      <span className="text-xl">{subcat.icon}</span>
                      <span className="flex-1">{subcat.name}</span>
                      <button
                        onClick={() => setEditingCategory(subcat.id)}
                        className="text-sm px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(subcat.id)}
                        className="text-sm px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default CategoryManager;
